import * as THREE from 'three'
import type { EntityHighlightTarget } from '../types/entity-highlight-target'
import {
  isImportedModelRenderProxyMesh,
  isImportedModelTopologyHost,
} from '../topology/imported-model-topology-runtime'
import { ImportedModelObjectMetadataService } from '../metadata'

/**
 * imported-model 高亮目标解析结果。
 * 说明：
 * 1. `object` 始终指向最终需要挂 overlay 的精确对象；
 * 2. `primitiveIndices` 仅在需要做 primitive 子集覆盖时存在；
 * 3. 该结构同时服务于 resolver 输出与 overlay renderer 输入，作为本轮 Task 2 的共享边界。
 */
export interface ImportedModelResolvedTarget {
  /** 最终命中的三维对象。 */
  object: THREE.Mesh | THREE.Line | THREE.Sprite
  /** 命中的 primitive 子集下标。 */
  primitiveIndices?: number[]
  /**
   * 命令态 overlay 的矩阵来源对象。
   *
   * 设计说明：导入拓扑 FeatureFace/FeatureLine/FeaturePoint 可能是按需代理，
   * 它们的父链会被拾取/保留链路重挂；真正稳定跟随预览和拖动的是可见 mesh
   * 或组件根。这里允许 resolver 显式声明矩阵来源，renderer 再按相对矩阵刷新。
   */
  matrixSourceObject?: THREE.Object3D
  /**
   * `object` 在 `matrixSourceObject` 坐标系下的局部矩阵。
   *
   * 该矩阵只在目标创建时分配一次，渲染前刷新时复用，避免在高频路径创建 Matrix4。
   */
  localMatrixToSource?: THREE.Matrix4
  /**
   * 无矩阵来源时使用的世界矩阵快照。
   *
   * 这是代理被回收或脱离父链后的兜底，不作为正常组件预览跟随策略。
   */
  worldMatrix?: THREE.Matrix4
}

interface GlbHierarchyMetaLike {
  hasDirectGeometry?: boolean
  highlightTargets?: EntityHighlightTarget[]
}

interface GlbHierarchyLike {
  meta?: GlbHierarchyMetaLike
}

/**
 * 展开指定对象，得到可直接渲染的 mesh target 列表。
 * 说明：
 * 1. 若命中 mesh，则直接返回该 mesh；
 * 2. 若命中 group，则展开其子树中的可渲染 mesh；
 * 3. 当 group 携带 primitiveIndices 时，按 descendant mesh 的 primitive span 做顺序映射，避免把父级 primitive 下标直接错误透传到子 mesh。
 *
 * @param targetObject 命中的对象。
 * @param primitiveIndices primitive 子集。
 * @returns 可渲染 mesh target 列表。
 */
export function expandImportedModelRenderableTargets(
  targetObject: THREE.Object3D,
  primitiveIndices?: number[],
): ImportedModelResolvedTarget[] {
  const normalizedPrimitiveIndices = normalizePrimitiveIndices(primitiveIndices)

  if (isImportedModelTopologyHost(targetObject)) {
    const renderProxyMeshes = targetObject.children.filter((child): child is THREE.Mesh => isImportedModelRenderProxyMesh(child))
    if (renderProxyMeshes.length > 0) {
      return renderProxyMeshes.map((mesh) => ({
        object: mesh,
        primitiveIndices: normalizedPrimitiveIndices,
      }))
    }
  }

  if (targetObject instanceof THREE.Mesh) {
    return [
      {
        object: targetObject,
        primitiveIndices: normalizedPrimitiveIndices,
      },
    ]
  }

  if (targetObject instanceof THREE.Line && !(targetObject instanceof THREE.LineSegments)) {
    return [
      {
        object: targetObject,
      },
    ]
  }

  const allDescendantMeshes = collectImportedModelDescendantMeshes(targetObject)
  if (allDescendantMeshes.length === 0) {
    return collectImportedModelDescendantLines(targetObject).map((line) => ({ object: line }))
  }

  if (!normalizedPrimitiveIndices) {
    return collectImportedModelRenderableMeshTargets(targetObject)
  }

  const primitiveIndexSet = new Set(normalizedPrimitiveIndices)
  const renderableMeshSet = new Set(collectImportedModelRenderableMeshes(targetObject))
  let primitiveOffset = 0
  const resolvedTargets: ImportedModelResolvedTarget[] = []

  allDescendantMeshes.forEach((mesh) => {
    const primitiveSpan = resolveMeshPrimitiveSpan(mesh)
    const localPrimitiveIndices: number[] = []

    for (let localPrimitiveIndex = 0; localPrimitiveIndex < primitiveSpan; localPrimitiveIndex += 1) {
      if (primitiveIndexSet.has(primitiveOffset + localPrimitiveIndex)) {
        localPrimitiveIndices.push(localPrimitiveIndex)
      }
    }

    if (localPrimitiveIndices.length > 0 && renderableMeshSet.has(mesh)) {
      resolvedTargets.push({
        object: mesh,
        primitiveIndices: localPrimitiveIndices.length < primitiveSpan ? localPrimitiveIndices : undefined,
      })
    }

    primitiveOffset += primitiveSpan
  })

  return resolvedTargets
}

/**
 * 从指定根节点中收集可渲染 mesh target。
 * 说明：
 * 1. 优先使用实体映射过滤，避免把辅助面/基准面纳入 part/assembly overlay；
 * 2. 若过滤后为空，则回退到所有 descendant mesh，保证不会把 group 静默传给 renderer 后什么都不显示。
 *
 * @param rootObject 收集根节点。
 * @returns mesh target 列表。
 */
export function collectImportedModelRenderableMeshTargets(
  rootObject: THREE.Object3D,
): ImportedModelResolvedTarget[] {
  const renderableMeshes = collectImportedModelRenderableMeshes(rootObject)
  if (renderableMeshes.length > 0) {
    return renderableMeshes.map((mesh) => ({ object: mesh }))
  }

  return collectImportedModelDescendantLines(rootObject).map((line) => ({ object: line }))
}

/**
 * 归一化 primitiveIndices。
 *
 * @param primitiveIndices 原始 primitive 下标。
 * @returns 过滤后的数组；若为空则返回 `undefined`。
 */
function normalizePrimitiveIndices(primitiveIndices?: number[]): number[] | undefined {
  if (!Array.isArray(primitiveIndices) || primitiveIndices.length === 0) {
    return undefined
  }

  const normalized = primitiveIndices.filter(
    (primitiveIndex) => Number.isInteger(primitiveIndex) && primitiveIndex >= 0,
  )

  return normalized.length > 0 ? normalized : undefined
}

/**
 * 收集可用于 overlay 的 mesh。
 *
 * @param rootObject 收集根节点。
 * @returns mesh 数组。
 */
function collectImportedModelRenderableMeshes(rootObject: THREE.Object3D): THREE.Mesh[] {
  const allMeshes = collectImportedModelDescendantMeshes(rootObject)
  const renderProxyMeshes = allMeshes.filter((mesh) => isImportedModelRenderProxyMesh(mesh))
  if (renderProxyMeshes.length > 0) {
    return renderProxyMeshes
  }
  if (rootObject instanceof THREE.Mesh) {
    return allMeshes
  }

  const mappedMeshes = allMeshes.filter((mesh) => shouldIncludeInHighlightOverlay(mesh))
  return mappedMeshes.length > 0 ? mappedMeshes : allMeshes
}

/**
 * 按原始遍历顺序收集 descendant mesh。
 *
 * @param rootObject 收集根节点。
 * @returns 原始 descendant mesh 数组。
 */
function collectImportedModelDescendantMeshes(rootObject: THREE.Object3D): THREE.Mesh[] {
  if (rootObject instanceof THREE.Mesh && !isImportedModelTopologyHost(rootObject)) {
    return [rootObject]
  }

  const allMeshes: THREE.Mesh[] = []

  rootObject.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    if (isImportedModelTopologyHost(object)) {
      return
    }

    allMeshes.push(object)
  })

  return allMeshes
}

/**
 * 按原始遍历顺序收集 descendant line。
 *
 * @param rootObject 收集根节点。
 * @returns 原始 descendant line 数组。
 */
function collectImportedModelDescendantLines(rootObject: THREE.Object3D): THREE.Line[] {
  if (rootObject instanceof THREE.Line && !(rootObject instanceof THREE.LineSegments)) {
    return shouldIncludeImportedModelLineInOverlay(rootObject) ? [rootObject] : []
  }

  const allLines: THREE.Line[] = []

  rootObject.traverse((object) => {
    if (!(object instanceof THREE.Line) || object instanceof THREE.LineSegments) {
      return
    }

    if (!shouldIncludeImportedModelLineInOverlay(object)) {
      return
    }

    allLines.push(object)
  })

  return allLines
}

/**
 * 判断 mesh 是否应纳入 part/assembly overlay。
 *
 * @param mesh 待判断 mesh。
 * @returns 是否纳入 overlay。
 */
function shouldIncludeInHighlightOverlay(mesh: THREE.Mesh): boolean {
  if (isImportedModelTopologyHost(mesh)) {
    return false
  }

  if (mesh.userData?.skipSelection) {
    return false
  }

  if (mesh.userData?.isSelectionHighlight || mesh.userData?.isImportedModelOverlay) {
    return false
  }

  const selfBodyEntries = mesh.userData?.importedPartBodyEntries
  if (Array.isArray(selfBodyEntries) && selfBodyEntries.length > 0) {
    return true
  }

  const selfPrimitiveEntries = mesh.userData?.primitiveEntityEntries
  if (Array.isArray(selfPrimitiveEntries) && selfPrimitiveEntries.length > 0) {
    return true
  }

  return hasMatchedParentPrimitiveEntityEntry(mesh)
}

/**
 * 判断 line 是否应纳入 overlay。
 *
 * @param line 待判断 line。
 * @returns 是否纳入 overlay。
 */
function shouldIncludeImportedModelLineInOverlay(line: THREE.Line): boolean {
  if (line.userData?.skipSelection) {
    return false
  }

  if (line.userData?.isSelectionHighlight || line.userData?.isImportedModelOverlay) {
    return false
  }

  return true
}

/**
 * 判断父节点 primitiveEntityEntries 是否与当前 mesh 的 sibling primitive 顺序匹配。
 *
 * @param mesh 待判断 mesh。
 * @returns 是否匹配。
 */
function hasMatchedParentPrimitiveEntityEntry(mesh: THREE.Mesh): boolean {
  const parent = mesh.parent
  if (!parent) {
    return false
  }

  const parentPrimitiveEntries = parent.userData?.primitiveEntityEntries
  if (!Array.isArray(parentPrimitiveEntries) || parentPrimitiveEntries.length === 0) {
    return false
  }

  const siblingPrimitiveIndex = resolveMeshSiblingPrimitiveIndex(mesh)
  if (siblingPrimitiveIndex === null) {
    return false
  }

  return parentPrimitiveEntries.some((entry) => {
    if (!entry || typeof entry !== 'object') {
      return false
    }

    const primitiveIndex = (entry as { primitiveIndex?: unknown }).primitiveIndex
    return typeof primitiveIndex === 'number' && primitiveIndex === siblingPrimitiveIndex
  })
}

/**
 * 解析 mesh 在同级 mesh 序列中的 primitive 下标。
 *
 * @param mesh 目标 mesh。
 * @returns primitive 下标。
 */
function resolveMeshSiblingPrimitiveIndex(mesh: THREE.Mesh): number | null {
  const parent = mesh.parent
  if (!parent) {
    return null
  }

  let meshSiblingIndex = 0
  for (const sibling of parent.children) {
    if (sibling === mesh) {
      return meshSiblingIndex
    }
    if (sibling instanceof THREE.Mesh) {
      meshSiblingIndex += 1
    }
  }

  return null
}

/**
 * 解析 mesh 覆盖的 primitive span。
 *
 * @param mesh 目标 mesh。
 * @returns primitive 个数。
 */
function resolveMeshPrimitiveSpan(mesh: THREE.Mesh): number {
  const geometry = mesh.geometry
  if (!(geometry instanceof THREE.BufferGeometry)) {
    return 1
  }

  return geometry.groups.length > 0 ? geometry.groups.length : 1
}

/**
 * 解析 imported-model 中装配体、零件与 component 的高亮目标集合。
 *
 * 设计说明：
 * 1. 先尝试消费 hierarchy.meta.highlightTargets，以便后续 controller 直接注入精确目标；
 * 2. 若当前节点没有显式目标，则回退到最近的语义根；
 * 3. 若语义根是 group，则展开为其直接可渲染 mesh 集合，供 overlay renderer 逐个挂载覆盖层。
 */
export class ImportedAssemblyPartTargetResolver {
  /**
   * 解析目标对象。
   *
   * @param targetObject 设计树或场景命中的 imported-model 目标。
   * @returns 可直接喂给 overlay renderer 的目标集合。
   */
  public resolve(targetObject: THREE.Object3D): ImportedModelResolvedTarget[] {
    const semanticRoot = this.resolveSemanticRoot(targetObject)
    const highlightTargets = this.readHighlightTargets(targetObject)
    const explicitTargets = this.resolveExplicitTargets(semanticRoot, targetObject, highlightTargets)

    if (explicitTargets.length > 0) {
      return explicitTargets
    }

    return collectImportedModelRenderableMeshTargets(semanticRoot)
  }

  /**
   * 读取 hierarchy.meta.highlightTargets。
   *
   * @param targetObject 输入对象。
   * @returns 可用高亮目标数组。
   */
  private readHighlightTargets(targetObject: THREE.Object3D): EntityHighlightTarget[] {
    const hierarchy = ImportedModelObjectMetadataService.resolveHierarchy(targetObject) as GlbHierarchyLike | undefined
    return Array.isArray(hierarchy?.meta?.highlightTargets) ? hierarchy.meta.highlightTargets : []
  }

  /**
   * 找到最近的语义根。
   *
   * @param targetObject 输入对象。
   * @returns 具备直接几何语义的根；若不存在则回退原对象。
   */
  private resolveSemanticRoot(targetObject: THREE.Object3D): THREE.Object3D {
    let current: THREE.Object3D | null = targetObject
    while (current) {
      const hierarchy = ImportedModelObjectMetadataService.resolveHierarchy(current) as GlbHierarchyLike | undefined
      if (hierarchy?.meta?.hasDirectGeometry) {
        return current
      }
      current = current.parent
    }
    return targetObject
  }

  /**
   * 解析显式声明的高亮目标。
   *
   * @param semanticRoot 语义根。
   * @param fallbackRoot 兜底起点。
   * @param highlightTargets 高亮目标描述。
   * @returns 命中的精确目标列表。
   */
  private resolveExplicitTargets(
    semanticRoot: THREE.Object3D,
    fallbackRoot: THREE.Object3D,
    highlightTargets: EntityHighlightTarget[],
  ): ImportedModelResolvedTarget[] {
    const resolvedTargets: ImportedModelResolvedTarget[] = []

    highlightTargets.forEach((highlightTarget) => {
      const matchedObject =
        this.findObjectByIdInSubtree(semanticRoot, highlightTarget.objectId)
        ?? this.findObjectByIdInSubtree(fallbackRoot, highlightTarget.objectId)
      if (!matchedObject) {
        return
      }

      resolvedTargets.push(
        ...expandImportedModelRenderableTargets(
          matchedObject,
          normalizePrimitiveIndices(highlightTarget.primitiveIndices),
        ),
      )
    })

    return resolvedTargets
  }

  /**
   * 在子树中按 uuid 查找对象。
   *
   * @param root 查找根节点。
   * @param objectId 目标 uuid。
   * @returns 命中的对象。
   */
  private findObjectByIdInSubtree(root: THREE.Object3D, objectId: string): THREE.Object3D | null {
    if (root.uuid === objectId) {
      return root
    }
    return root.getObjectByProperty('uuid', objectId) ?? null
  }

}
